import { FileText, ChevronDown, Loader2 } from 'lucide-react';
import { useState } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../i18n/config';
import { useFetchRelevantDocumentsMutation } from '../../common/backend-api/backend-api';
import RelevantDocumentItem from './RelevantDocumentItem';

interface RelevantDocumentsProps {
  relevantDocumentIds?: Record<string, {
    relevance: 'highly_relevant' | 'moderately_relevant' | 'slightly_relevant' | 'not_relevant';
    contentType: 'case_law' | 'statute' | 'regulation' | 'legal_analysis' | 'procedural' | 'other';
    reasoning?: string;
    confidence?: number;
    keyTerms?: string[];
  }>;
  messageId: string;
}

const RelevantDocuments: React.FC<RelevantDocumentsProps> = ({ relevantDocumentIds, messageId }) => {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const isRtl = getLanguageDirection(i18n.language) === 'rtl';

  const [fetchRelevantDocuments, { isLoading }] = useFetchRelevantDocumentsMutation();

  if (!relevantDocumentIds || Object.keys(relevantDocumentIds).length === 0) {
    return null;
  }

  const handleExpand = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded && !hasFetched) {
      const documentIds = Object.keys(relevantDocumentIds);
      fetchRelevantDocuments({ documentIds });
      setHasFetched(true);
    }
  };

  const documentCount = Object.keys(relevantDocumentIds).length;
  const labelKey = documentCount === 1
    ? 'components.message.relevantSources_one'
    : 'components.message.relevantSources_other';
  const label = t(labelKey).replace('{{count}}', documentCount.toString());

  return (
    <div className="mt-2">
      {/* Toggle button */}
      <button
        onClick={handleExpand}
        dir={getLanguageDirection(i18n.language)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border/40 bg-card/50 text-muted-foreground text-sm font-medium hover:bg-gold/[0.06] hover:text-gold hover:border-gold/30 transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}
      >
        <FileText size={15} />
        <span>{label}</span>
        <ChevronDown
          size={15}
          className="transition-transform duration-200"
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {/* Collapsible panel */}
      {isExpanded && (
        <div className="mt-1.5">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center gap-2 py-6 bg-card/40 rounded-lg border border-border/10">
              <Loader2 size={20} className="animate-spin text-gold" />
              <p className="text-sm font-medium text-muted-foreground">
                {t('components.message.loadingDocuments')}
              </p>
            </div>
          )}

          {/* Documents list */}
          {!isLoading && hasFetched && Object.keys(relevantDocumentIds).length > 0 && (
            <div className="flex flex-col gap-2">
              {Object.entries(relevantDocumentIds).map(([documentId]) => (
                <RelevantDocumentItem
                  key={documentId}
                  documentId={documentId}
                  messageId={messageId}
                />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && hasFetched && Object.keys(relevantDocumentIds).length === 0 && (
            <div className="text-center py-6 bg-card/40 rounded-lg border border-border/10">
              <p className="text-sm font-medium text-muted-foreground">
                {t('components.message.noDocumentsFound')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RelevantDocuments;
